import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
} from '@mui/material';
import {
  MoreVert,
  Delete,
  DeleteForever,
  PersonRemove,
} from '@mui/icons-material';
import DialogContentText from '@mui/material/DialogContentText';
import { useTheme } from '@mui/material/styles';

const MessageBox = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);

  const resellerId = localStorage.getItem('resellerId');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 🔹 Reusable fetch function
const fetchMessages = async () => {
  if (!resellerId) return;
  try {
    const response = await fetch(`https://reviewhub-lc.onrender.com/api/messages/${resellerId}`);
    const data = await response.json();
    setMessages(data);
  } catch (error) {
    console.error("Error fetching messages:", error);
  }
};

useEffect(() => {
  fetchMessages();
}, [resellerId]);

const handleSubmit = async () => {
  if (message.trim() === "") return;

  if (!resellerId) {
    alert("Reseller not logged in");
    return;
  }

  try {
    if (editIndex !== null) {
      // --- Update ---
      const msgToUpdate = messages[editIndex];
      await fetch(`https://reviewhub-lc.onrender.com/api/messages/${msgToUpdate._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message }),
      });
    } else {
      // --- New message ---
      await fetch("https://reviewhub-lc.onrender.com/api/messages/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resellerId, message }),
      });
    }

    // 🔑 Always refetch from DB after save
    await fetchMessages();

    setMessage("");
    setOpen(false);
    setEditIndex(null);
  } catch (err) {
    alert("Error submitting message");
  }
};

  const handleOpen = () => {
    setOpen(true);
    setMessage('');
    setEditIndex(null);
  };

  const handleClose = () => {
    setOpen(false);
    setMessage('');
    setEditIndex(null);
  };


  const handleMenuOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setMenuIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuIndex(null);
  };

  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteForMe = async () => {
    const msgToDelete = messages[deleteIndex];
    try {
      await fetch(`https://reviewhub-lc.onrender.com/api/messages/delete-for-me/${msgToDelete._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resellerId }),
      });

      setMessages(messages.filter((_, i) => i !== deleteIndex));
    } catch (error) {
      alert('Error deleting for me');
    }

    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleDeleteForEveryone = async () => {
    const msgToDelete = messages[deleteIndex];
    try {
      const response = await fetch(`https://reviewhub-lc.onrender.com/api/messages/${msgToDelete._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete');

      setMessages(messages.filter((_, i) => i !== deleteIndex));
    } catch (error) {
      alert('Error deleting message');
    }
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Send Message to Your Followers
      </Typography>

      <Button variant="contained" onClick={handleOpen} sx={{ mb: 3 ,   backgroundColor: '#616161',}}>
        Send Message
      </Button>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editIndex !== null ? 'Edit Message' : 'Send Message'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Type your message"
            fullWidth
            variant="outlined"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editIndex !== null ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Display */}
      {messages.length > 0 && (
        isMobile ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {messages.map((msg, index) => (
            <Card key={msg._id} sx={{ boxShadow: 2 }}>
  <CardContent>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {msg.message}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(msg.createdAt).toLocaleString()}
        </Typography>
      </Box>
      <IconButton onClick={(e) => handleMenuOpen(e, index)} size="small">
        <MoreVert />
      </IconButton>
    </Box>
  </CardContent>
</Card>

            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((msg, index) => (
                  <TableRow key={msg._id} hover>
                    <TableCell>{msg.message}</TableCell>
                    <TableCell>{new Date(msg.createdAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, index)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDeleteClick(menuIndex)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Dialog */}
         <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile} // 👈 Full screen on mobile
      sx={{
        '& .MuiDialogTitle-root': {
          textTransform: 'none', // 🔤 no caps
          fontSize: '1.2rem',
          fontWeight: 500,
        },
        '& .MuiDialogContentText-root': {
          textTransform: 'none', // 🔤 no caps
          fontSize: '1rem',
        },
        '& .MuiButton-root': {
          textTransform: 'none', // 🔤 no caps
        },
      }}
    >
      <DialogTitle>Delete message</DialogTitle>

      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete this message?
        </DialogContentText>
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 1,
          px: 3,
          pb: 2,
        }}
      >
        <Button
          onClick={() => setDeleteDialogOpen(false)}
          variant="outlined"
          fullWidth={isMobile}
        >
          Cancel
        </Button>

        <Button
          onClick={handleDeleteForMe}
          variant="contained"
          startIcon={<PersonRemove />}
          fullWidth={isMobile}
        >
          for me
        </Button>

        <Button
          onClick={handleDeleteForEveryone}
          variant="contained"
          color="error"
          startIcon={<DeleteForever />}
          fullWidth={isMobile}
        >
          for everyone
        </Button>
      </DialogActions>
    </Dialog>
    </Box>
  );
};

export default MessageBox;
